import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Logo } from "../components/Logo";
import { Heart, Upload, ArrowLeft, Check } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

const LIENS = [
  { value: "conjoint", label: "Conjoint(e)" },
  { value: "enfant", label: "Enfant" },
  { value: "parent", label: "Parent" },
  { value: "frere_soeur", label: "Frère / Sœur" },
  { value: "petit_enfant", label: "Petit-enfant" },
  { value: "neveu_niece", label: "Neveu / Nièce" },
  { value: "autre", label: "Autre (préciser dans le message)" },
];

export default function AccesHeritier() {
  const [form, setForm] = useState({
    defunt_email: "", defunt_nom_complet: "", defunt_date_deces: "",
    demandeur_prenom: "", demandeur_nom: "", demandeur_email: "",
    demandeur_telephone: "", lien_avec_defunt: "", message: "",
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!file) { setError("Veuillez joindre l'acte de décès."); return; }
    setSubmitting(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      data.append("acte_deces", file);
      await axios.post(`${API}/heritier/request`, data, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur lors de la soumission.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 py-12" style={{ backgroundColor: "var(--auprea-cream)" }}>
        <div className="w-full max-w-lg">
          <Link to="/" className="block mb-10 text-center"><Logo size={36} /></Link>
          <div className="auprea-card">
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
              <div style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(47,125,91,0.12)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Check size={28} style={{ color: "var(--auprea-green)" }} />
              </div>
            </div>
            <h1 style={{ fontSize: 26, marginBottom: 12, textAlign: "center" }} data-testid="heritier-success">Demande reçue</h1>
            <p style={{ color: "rgba(35,38,46,0.75)", lineHeight: 1.65, fontSize: 15 }}>
              Nous vous présentons nos sincères condoléances. Votre demande d'accès au carnet AUPREA va être étudiée
              par notre équipe sous <strong>48 heures ouvrées</strong>. Nous vérifions chaque demande manuellement,
              dans le respect du défunt et de la confidentialité absolue du carnet.
            </p>
            <p style={{ color: "rgba(35,38,46,0.7)", fontSize: 14, marginTop: 16 }}>
              Vous recevrez un email à <strong>{form.demandeur_email}</strong> contenant la suite de la procédure.
            </p>
            <Link to="/" className="auprea-btn w-full inline-block text-center mt-6">Retour à l'accueil</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-6 py-12" style={{ backgroundColor: "var(--auprea-cream)" }}>
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="flex items-center gap-2 mb-6" style={{ color: "var(--auprea-blue)", fontSize: 14 }}>
          <ArrowLeft size={16} /> Retour à l'accueil
        </Link>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}><Logo size={32} /></div>
        <div className="auprea-card">
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <Heart size={28} style={{ color: "var(--auprea-gold)" }} />
          </div>
          <h1 style={{ fontSize: 30, marginBottom: 10, textAlign: "center" }}>Demande d'accès héritier</h1>
          <p style={{ color: "rgba(35,38,46,0.75)", marginBottom: 28, lineHeight: 1.65, fontSize: 15, textAlign: "center" }}>
            Suite au décès d'un proche utilisateur AUPREA, vous pouvez demander un accès en lecture seule à son carnet.
            Toute demande est vérifiée manuellement par notre équipe sous 48 heures ouvrées.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <fieldset className="border-t pt-4" style={{ borderTopColor: "rgba(21,41,79,0.1)" }}>
              <legend style={{ fontSize: 11, color: "var(--auprea-gold)", letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, padding: "0 8px" }}>— La personne défunte</legend>
              <div className="grid md:grid-cols-2 gap-4 mt-3">
                <Field label="Nom et prénom du défunt *" value={form.defunt_nom_complet} onChange={(v) => setForm({ ...form, defunt_nom_complet: v })} required testid="heritier-defunt-nom" />
                <Field label="Email AUPREA du défunt *" type="email" value={form.defunt_email} onChange={(v) => setForm({ ...form, defunt_email: v })} required testid="heritier-defunt-email" />
                <Field label="Date du décès *" type="date" value={form.defunt_date_deces} onChange={(v) => setForm({ ...form, defunt_date_deces: v })} required testid="heritier-date" />
              </div>
            </fieldset>

            <fieldset className="border-t pt-4" style={{ borderTopColor: "rgba(21,41,79,0.1)" }}>
              <legend style={{ fontSize: 11, color: "var(--auprea-gold)", letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, padding: "0 8px" }}>— Vous-même</legend>
              <div className="grid md:grid-cols-2 gap-4 mt-3">
                <Field label="Prénom *" value={form.demandeur_prenom} onChange={(v) => setForm({ ...form, demandeur_prenom: v })} required testid="heritier-prenom" />
                <Field label="Nom *" value={form.demandeur_nom} onChange={(v) => setForm({ ...form, demandeur_nom: v })} required testid="heritier-nom" />
                <Field label="Votre email *" type="email" value={form.demandeur_email} onChange={(v) => setForm({ ...form, demandeur_email: v })} required testid="heritier-email" />
                <Field label="Téléphone *" type="tel" value={form.demandeur_telephone} onChange={(v) => setForm({ ...form, demandeur_telephone: v })} required testid="heritier-tel" />
                <div>
                  <label className="auprea-label">Lien avec le défunt *</label>
                  <select className="auprea-input" value={form.lien_avec_defunt} onChange={(e) => setForm({ ...form, lien_avec_defunt: e.target.value })} required data-testid="heritier-lien">
                    <option value="">— Choisir —</option>
                    {LIENS.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
                  </select>
                </div>
              </div>
            </fieldset>

            <fieldset className="border-t pt-4" style={{ borderTopColor: "rgba(21,41,79,0.1)" }}>
              <legend style={{ fontSize: 11, color: "var(--auprea-gold)", letterSpacing: "0.2em", textTransform: "uppercase", fontWeight: 600, padding: "0 8px" }}>— Justificatif</legend>
              <div className="mt-3">
                <label className="auprea-label">Acte de décès (PDF, JPG ou PNG, max 10 Mo) *</label>
                <div style={{ background: "rgba(243,239,230,0.6)", border: "1px dashed rgba(21,41,79,0.25)", padding: "20px", borderRadius: 4, display: "flex", alignItems: "center", gap: 14, cursor: "pointer" }} onClick={() => document.getElementById("acte-input").click()}>
                  <Upload size={20} style={{ color: "var(--auprea-gold)" }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, color: "var(--auprea-ink)" }}>
                      {file ? file.name : "Cliquez pour téléverser l'acte de décès"}
                    </div>
                    {file && <div style={{ fontSize: 12, color: "rgba(35,38,46,0.6)" }}>{(file.size / 1024).toFixed(0)} Ko</div>}
                  </div>
                </div>
                <input id="acte-input" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setFile(e.target.files[0])} style={{ display: "none" }} data-testid="heritier-file" />
              </div>
              <div className="mt-4">
                <label className="auprea-label">Message complémentaire (facultatif)</label>
                <textarea className="auprea-input" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="heritier-message" />
              </div>
            </fieldset>

            {error && (
              <div style={{ color: "#b53a3a", fontSize: 14, padding: "10px 12px", background: "#fcf3f3", border: "1px solid #f0c5c5" }} data-testid="heritier-error">
                {error}
              </div>
            )}

            <div style={{ background: "rgba(202,168,106,0.08)", padding: "12px 16px", fontSize: 13, color: "rgba(35,38,46,0.75)", borderRadius: 4, lineHeight: 1.6 }}>
              🔒 Toutes les demandes sont vérifiées manuellement. Aucune information du carnet du défunt n'est partagée
              tant que votre demande n'a pas été validée par notre équipe.
            </div>

            <button type="submit" className="auprea-btn w-full" disabled={submitting} data-testid="heritier-submit">
              {submitting ? "Envoi en cours…" : "Envoyer ma demande"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="auprea-label">{label}</label>
      <input type={type} className="auprea-input" value={value} onChange={(e) => onChange(e.target.value)} required={required} data-testid={testid} />
    </div>
  );
}
